/**
 * Une unité de travail de la file du matin (T-1).
 *
 * Anatomie imposée par la conception (§10.2) et la maquette v5 : un rang, le
 * type écrit EN TOUTES LETTRES (jamais porté par la couleur seule), le motif —
 * une phrase rédigée par le serveur —, l'ancrage daté qui la rend contestable,
 * puis un pied à trois éléments dans cet ordre de lecture : UNE action, une
 * sortie, la durée.
 *
 * TOUT LE TEXTE VIENT DU SERVEUR : `motif`, `ancrage[]`, `action.libelle`. Le
 * client ne reformule rien — il ne choisit que la destination du bouton, à
 * partir de `action.code` et de `cible`.
 *
 * Deux règles non négociables tenues ici :
 *  - un `signal_securite` porte le seul rouge de toute la console, et son
 *    absence de bouton « Plus tard » est ÉCRITE, pas seulement absente
 *    (`reportable: false` — la sécurité d'un mineur ne se reporte pas) ;
 *  - aucun score, aucun pourcentage, aucun « niveau de priorité » : le rang est
 *    l'ordre décidé par le serveur, rien d'autre.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { UniteTravailDto } from '@silex/shared';

import { ChipTemps } from '../components/ChipTemps';
import type { LectureDUnite } from '../lib/ma-journee-manuel';
import {
  formatDuree,
  libelleNiveauScolaire,
  libelleTypeUnite,
  matiereConsole,
} from '../lib/tuteur-format';

/** Le filet de gauche et la pastille de rang suivent le type, jamais l'inverse. */
function classesDuType(type: UniteTravailDto['type']): { carte: string; ord: string } {
  if (type === 'signal_securite') {
    return { carte: 'card wq-item wq-item--securite', ord: 'wq-ord wq-ord--rare' };
  }
  if (type === 'evaluation_a_valider') {
    return { carte: 'card wq-item', ord: 'wq-ord' };
  }
  return { carte: 'card wq-item wq-item--watch', ord: 'wq-ord wq-ord--watch' };
}

function classeBadge(type: UniteTravailDto['type']): string {
  if (type === 'signal_securite') return 'badge badge--rare';
  if (type === 'evaluation_a_valider') return 'badge badge--new';
  return 'badge badge--a-revoir';
}

/**
 * Destination de l'action, dérivée de `action.code`. `ouvrir_signal` n'a pas de
 * destination : aucune route n'expose le détail d'une alerte à un tuteur (cf.
 * le panneau ci-dessous, qui le dit).
 */
function destination(unite: UniteTravailDto): string | null {
  switch (unite.action.code) {
    case 'valider_evaluation':
      return unite.cible.evaluationId ? `/validation/${unite.cible.evaluationId}` : null;
    case 'creer_exercices_cibles': {
      const params = new URLSearchParams({ eleve: unite.cible.eleveId });
      if (unite.cible.savoirId) params.set('savoir', unite.cible.savoirId);
      if (unite.matiere) params.set('matiere', unite.matiere);
      return `/exercices/nouveau?${params.toString()}`;
    }
    case 'ecrire_mot_du_tuteur':
      return `/rapports/${unite.cible.eleveId}`;
    case 'ouvrir_signal':
    default:
      return null;
  }
}

export interface UniteTravailCarteProps {
  readonly unite: UniteTravailDto;
  readonly traitee: boolean;
  readonly enCours: boolean;
  readonly onReporter: (cle: string) => void;
  readonly onMarquerTraitee: (cle: string) => void;
  readonly onRouvrir: (cle: string) => void;
  /**
   * Φ-MV-7 (`UX-7-03`, `UX-7-14`, décision F-32) — l'ancien parcours en pause :
   * le motif et l'ancrage sans code interne, et où mène « Ouvrir sa fiche »
   * (`lib/ma-journee-manuel.ts`). Absente : la carte est celle d'avant.
   */
  readonly lecture?: LectureDUnite;
}

export function UniteTravailCarte({
  unite,
  traitee,
  enCours,
  onReporter,
  onMarquerTraitee,
  onRouvrir,
  lecture,
}: UniteTravailCarteProps) {
  const [signalOuvert, setSignalOuvert] = useState(false);
  const classes = classesDuType(unite.type);
  const lien = destination(unite);
  const estSignal = unite.type === 'signal_securite';

  return (
    <article className={traitee ? `${classes.carte} wq-item--traitee` : classes.carte}>
      <span className={classes.ord} aria-hidden="true">
        {unite.rang}
      </span>
      <div>
        <div className="wq-head">
          <span className="wq-eleve">{unite.elevePrenom}</span>
          <span className={classeBadge(unite.type)}>{libelleTypeUnite(unite.type)}</span>
          <span className="wq-contexte">
            {libelleNiveauScolaire(unite.eleveNiveauScolaire)}
            {unite.matiere ? ` · ${matiereConsole(unite.matiere)}` : ''}
          </span>
          {traitee ? <span className="badge badge--acquis">Traitée</span> : null}
        </div>

        {/* Rédigé par le serveur — affiché tel quel, jamais reformulé. */}
        <p className="wq-motif">{lecture?.motif ?? unite.motif}</p>
        <p className="wq-ancrage">{(lecture?.ancrage ?? unite.ancrage).join(' · ')}</p>

        {signalOuvert ? (
          <div className="callout callout--rare wq-signal" role="note">
            <strong>Le détail de l'alerte n'est pas servi à la console.</strong> Ce que Silex expose
            au tuteur, c'est le motif et l'ancrage ci-dessus. Le contenu du message et la marche à
            suivre jointe à l'alerte ne sont accessibles par aucune route de l'API à ce jour ; le
            parent titulaire, lui, a été prévenu — l'ancrage le dit. Cette unité n'est pas
            reportable.
          </div>
        ) : null}

        <div className="wq-actions">
          {lien ? (
            <Link className="btn btn--primary btn--sm" to={lien}>
              {unite.action.libelle}
            </Link>
          ) : (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => setSignalOuvert((ouvert) => !ouvert)}
            >
              {signalOuvert ? 'Replier le signal' : unite.action.libelle}
            </button>
          )}

          {!estSignal ? (
            <Link
              className="btn btn--quiet btn--sm"
              to={lecture?.fiche ?? `/eleves/${unite.cible.eleveId}`}
            >
              Ouvrir sa fiche
            </Link>
          ) : null}

          {traitee ? (
            <button
              type="button"
              className="btn btn--quiet btn--sm"
              onClick={() => onRouvrir(unite.cle)}
            >
              Rouvrir
            </button>
          ) : /* Un signal de sécurité ne se clôt qu'une fois OUVERT : on ne
                 écarte pas d'un clic une alerte qu'on n'a pas lue. */
          !estSignal || signalOuvert ? (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => onMarquerTraitee(unite.cle)}
            >
              C'est fait
            </button>
          ) : null}

          {unite.reportable ? (
            <button
              type="button"
              className="btn btn--quiet btn--sm"
              onClick={() => onReporter(unite.cle)}
              disabled={enCours}
            >
              {enCours ? 'Report en cours…' : 'Plus tard'}
            </button>
          ) : (
            <span className="wq-note">Cette unité n'est pas reportable.</span>
          )}

          <ChipTemps aDroite>{formatDuree(unite.dureeEstimeeMinutes)}</ChipTemps>
        </div>
      </div>
    </article>
  );
}
